import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { memo, useState } from "react";
import { bulkDeleteDrafts } from "../client/conversations";
import { getNextBatch } from "../utils";
import * as deleteDraftsModalCss from "./delete-drafts-modal.module.css";

export const DeleteDraftsModal = memo(function DeleteDraftsModal({
  open,
  setOpen,
  conversations,
  cleanupFn,
  selectAllInfo,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
  };
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [deletingSelectAll, setDeletingSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);

  const handleDeleteDrafts = async () => {
    if (conversations.length > 0) {
      const conversationIds = conversations.map(
        (conversation) => conversation._id,
      );
      const channels = [
        ...new Set(conversations.map((conversation) => conversation.platform)),
      ];
      await bulkDeleteDrafts(client, { conversationIds });
      amplitude.logEvent("delete-drafts", {
        mode: "multiple",
        conversationIds,
        channels,
      });
    }
    cleanupFn?.(true);
    setOpen(false);
  };

  const deleteBatch = async (): Promise<{
    batchSize: number;
    done: boolean;
  }> => {
    if (!selectAllInfo) {
      return { batchSize: 0, done: true };
    }
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      await bulkDeleteDrafts(client, { conversationIds });
    }
    return { batchSize: value.conversations.length, done: false };
  };

  const handleDeleteSelectAll = async () => {
    if (!selectAllInfo) {
      return;
    }
    selectAllInfo.setBlockRefresh(true);
    setDeletingSelectAll(true);
    let done = false;
    do {
      const result = await deleteBatch();
      setNumDone((prevNumDone) => prevNumDone + result.batchSize);
      done = result.done;
    } while (!done);
    amplitude.logEvent("delete-drafts", { mode: "all" });
    setDeletingSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.(true);
    setOpen(false);
  };

  const plural = conversations.length > 1 || selectAllInfo;

  return (
    <RedoModal
      headerBorder={false}
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => {
        cleanupFn?.(false);
        setOpen(false);
      }}
      primaryButton={
        !deletingSelectAll
          ? {
              onClick: async () => {
                if (selectAllInfo) {
                  await handleDeleteSelectAll();
                } else {
                  await handleDeleteDrafts();
                }
              },
              text: `Yes, delete draft${plural ? "s" : ""}`,
              theme: "destructive",
            }
          : undefined
      }
      secondaryButton={
        !deletingSelectAll
          ? { onClick: () => setOpen(false), text: "Cancel" }
          : undefined
      }
      theme="error"
      title={
        deletingSelectAll
          ? "Deleting drafts"
          : `Delete ${
              selectAllInfo
                ? "all selected "
                : plural
                  ? conversations.length + " "
                  : ""
            }draft${plural ? "s" : ""}?`
      }
    >
      {deletingSelectAll ? (
        <div className={deleteDraftsModalCss.progressContainer}>
          <RedoProgressBar
            percentage={(numDone / (selectAllInfo?.count || numDone)) * 100}
          />
        </div>
      ) : (
        <p>
          Are you sure you want to delete {plural ? "these " : "this "}draft
          {plural ? "s" : ""}?
        </p>
      )}
    </RedoModal>
  );
});
